import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

import type {
  ChoirAttendanceSummary,
  ChoirBrief,
  MyAttendance,
  PastSession,
  SessionKind,
  VoicePartAttendance,
} from '@contracts/index';

import { ApiError, api } from '../api';
import { formatLongDate, joinDates, missesInARow, plural, voicePartLetter, words } from '../format';
import { useHome } from '../home';
import Avatar, { AVATAR_ROW } from './Avatar';
import InviteCard from './InviteCard';
import { LADDER, rungJustReached } from './ladder';
import MyProfileCard, { ProfileScreen, RemindersRow } from './MyProfileCard';
import Push from './Push';
import { Empty, Failed, Settling, Skeleton } from './States';
import UndoBar, { type Done } from './UndoBar';

/**
 * **«Я», from `legacy/frontend/src/screens/MeScreen.tsx` — a region port, built up task by
 * task** (`docs/specs/features/F-021/plan.md` T5, T6, T8, T9).
 *
 * What is here is the container as T5 and T6 draw it: `SummaryCard` (`:298-338`), the
 * screen's own doc comment and signature (`:340-360`), the four answers and `failure`
 * (`:373-377`), `kind` with the comment that says «four requests» (`:378-387`, `FND-810`),
 * `page`, `useHome` (`:388-390`), `load()` whole with both its comments (`:392-419`, `FND-810`),
 * the effect that runs it once per mount (`:421-423`), `pushed()` (`:427-432`), the `profile` arm
 * that returns early (`:434-445`), `shownSummary` and `hasServices` (`:448-452`),
 * `MyProfileCard` with the comment over it (`:462-466`), the segment (`:468-483`), the
 * skeleton and the refusal (`:485-490`), and the choir's anonymous totals with the comment
 * over them (`:501-504`, `FND-810`).
 *
 * T8 adds the figures themselves: `statusLabel` with its comment (`:120-133`), `MyFigures`
 * (`:182-296`) — the empty card, the hero, the last ten and the tiles, with every comment
 * inside it — `shown` (`:447`) and the `<Settling><MyFigures /></Settling>` block that reads
 * it (`:491-495`).
 *
 * T9 adds the rule: `RuleNote` and `RuleCard` with the doc comment over the pair (`:135-180`,
 * the comment inside the card at `:167-170` included), the binding line `  rule,` (`:196`) and
 * the two call sites that read it, each with its own comment — the footnote inside the hero
 * (`:249-252`) and the card under it (`:255-259`).
 *
 * T10 adds the two celebrations and closes the last hole of the region: `MILESTONES` with its
 * comment (`:30-40`), `StreakMilestone` (`:42-72`) and `RungOpened` (`:74-118`), every comment
 * with them — the doc comment at `:49-53` («identical banners») is `FND-810`'s second item,
 * `KEEP`, and is ported as it stands, and the three comments inside `RungOpened`'s JSX
 * (`:89-97`) come with it; then the two call sites, `StreakMilestone`'s last inside `.mine`
 * (`:291-293`) and `RungOpened`'s first in the fragment (`:456-460`), each with its own
 * comment.
 *
 * **F-019** (`docs/specs/features/F-019/plan.md` T5) adds the invite card and the bar its
 * rotation raises, which were this region's two F-019 holes: `useRef` in the `react` import
 * (`:1`), the `InviteCard` and `UndoBar` imports (`:23`, `:28`), `done`, `acts` and `report`
 * with the comments over them (`:361-372`, F-021's **G2**), and, under the choir's totals,
 * `InviteCard` with its comment and the `UndoBar` that closes the fragment (`:505-513`).
 * Nothing came with them. In particular there is no effect on `page`, because the old file has
 * none: the bar outlives a trip to «Профиль» and, on the way back, answers a card that has been
 * mounted again — `FND-835`, `KEEP`.
 *
 * **F-022** (`docs/specs/features/F-022/plan.md` T7) fills the three holes F-021 left for «Профиль»,
 * and adds nothing beside them: `ProfileScreen` in the `MyProfileCard` import (`:25`), the
 * `onProfileChanged` prop in the destructuring and the props' type (`:353`, `:358`), `rate` with the
 * blank line under it (`:425`), and the `profile` arm as the old file has it (`:434-445`), pushing
 * `ProfileScreen` with its six props where F-021's marked placeholder and its comment stood (**G1**
 * and **G3** of F-021's STOP gate).
 *
 * **F-030** (`docs/specs/features/F-030/plan.md` T8) closes the last hole of the region: the
 * `RemindersRow` import (`:25`) and the row itself with its comment, last under the figures and
 * above the choir's totals (`:497-499`). It is mounted ungated, as the old file mounts it — not
 * by role, not by the attendance, not by the segment — and nothing came with it: no `key`, no
 * effect, no refresh call and no state lifted out of the row (`FND-1505`, `FND-1504`).
 *
 * Everything else on the old screen is absent, and absent rather than written smaller. Each
 * with the row that owns it:
 *
 *   * **`askWriteAccess`** (`MyProfileCard.tsx:675-737`) — the platform call `RemindersRow`
 *     makes on a press of «Разрешить». The row is code in this file now, but the call it makes
 *     is not: it lives inside the row, which is `MyProfileCard.tsx`'s. **F-030's**, build spec
 *     §0.2.
 *
 * The imports differ from the old file's on purpose: the server shapes come from
 * `@contracts/index` (ADR-0011) rather than from `../api`, which is where the old client kept
 * its own copy of each of them.
 *
 * The regions of the old file this one reproduces line for line are on
 * `scripts/ported-partial.txt`.
 */

/**
 * The milestones, celebrated once and quietly: a washed card with one ring
 * going out, no confetti and no guilt. Nothing about «shown» is stored — the
 * card is visible exactly while the streak stands on a milestone and leaves
 * with the next rehearsal on its own.
 */
const MILESTONES: Record<number, { word: string; next: string | null }> = {
  10: { word: 'десять', next: 'двадцать пять' },
  25: { word: 'двадцать пять', next: 'пятьдесят' },
  50: { word: 'пятьдесят', next: 'сто' },
  100: { word: 'сто', next: null },
};

/**
 * The milestone, and only when the streak is standing on one.
 *
 * The card that used to state the streak in full went into the tiles above:
 * «7 · серия · рекорд 11» says the same thing in a quarter of the room. What is
 * left is the celebration, which is the part a figure cannot carry.
 *
 * It names the kind it counts, because there are two of them and the badge
 * cannot say which: somebody with a streak in rehearsals and another in
 * services was getting «Веха: десять подряд» twice, in identical banners.
 */
function StreakMilestone({ stats }: { stats: MyAttendance }) {
  const milestone = MILESTONES[stats.streak];
  if (milestone === undefined) return null;
  return (
    <div className="milestone card--toned">
      <span className="milestone-badge">
        <span className="num">{stats.streak}</span>
      </span>
      <span className="live-body">
        <span className="live-name">
          Веха: {milestone.word} {words(stats.kind).genitivePlural} подряд
        </span>
        {milestone.next !== null && (
          <span className="live-sub">следующая — {milestone.next}</span>
        )}
      </span>
    </div>
  );
}

/**
 * The rung the streak has just landed on, celebrated once and quietly.
 *
 * The same card and the same rule as the milestone above: nothing about «shown»
 * is stored, it is visible exactly while the streak stands on the rung, and it
 * leaves with the next rehearsal on its own. What it adds is the thing that is
 * now in the person's hands — the eyes or the colour that was dashed on
 * «Профиль» this morning and is not any more — and where to put it on.
 */
function RungOpened({ choir, stats }: { choir: ChoirBrief; stats: MyAttendance }) {
  const rung = rungJustReached(stats.streak);
  if (rung === undefined) return null;
  const after = LADDER[LADDER.indexOf(rung) + 1];
  return (
    <div className="milestone milestone--rung card--toned">
      {/* The face itself, jumping: what has changed is the face, so the badge
          of a figure the milestone card carries would be about the wrong thing. */}
      {/* No answer to a finger here: this face is already jumping, and the
          jump is the point of the card. */}
      {/* A row's size and not the large one since 2026-09-04. The plaque stands
          over the card that names the person, and two faces of 64 on one screen
          made the top one - which is about a piece of news, not about a face -
          compete with the one that has the name beside it. The jump is what the
          plaque has to say and it keeps it; the size is what it gives up. */}
      <Avatar
        eyes={choir.avatar_eyes}
        color={choir.avatar_color}
        rate={stats.attendance_rate_30d}
        size={AVATAR_ROW}
        move="hop"
        tappable={false}
      />
      <span className="live-body">
        <span className="live-name">
          {rung.word} подряд — {rung.gift}
        </span>
        <span className="live-sub">
          {after === undefined
            ? 'лестница пройдена'
            : `дальше ${after.gift.split(' — ')[0]}, через ${after.at - rung.at}`}
        </span>
      </span>
    </div>
  );
}

/**
 * The word for one of the last ten, carried as a title on its strip.
 *
 * The app's one attendance vocabulary - see «Attendance vocabulary» in
 * `DESIGN.md`. «уважительно» hangs off the same dot separator «рег» does, which
 * is what the roster already uses for a qualifier on a verdict.
 */
function statusLabel(item: PastSession): string {
  if (item.status === 'on_time') return 'вовремя';
  if (item.status === 'late') {
    return item.late_minutes === null ? 'опоздание' : `опоздание ${item.late_minutes} мин`;
  }
  return item.is_excused ? 'пропуск · уважительно' : 'пропуск';
}

/**
 * The rule, when it has something to say about the person holding the phone.
 *
 * Two shapes and the reason they differ is what each one is: one miss is a
 * footnote to the figure above it, so it is a line inside the same card; two
 * in a row is a fact about the next service, so it is a card of its own in the
 * colour a miss already has everywhere in this app - the matrix cell, the
 * strip of the last ten, the roster's status.
 *
 * Red and not amber, which is `DESIGN.md`'s rule of 2026-08-21 rather than a
 * choice made here: «Я» always carries the gold, and the two must not meet.
 */
function RuleNote({ stats }: { stats: MyAttendance }) {
  if (stats.barred || stats.misses_now === 0) return null;
  return (
    <p className="hero-note">
      Пропущена спевка {joinDates(stats.missed_starts)}. Ещё один пропуск подряд — и на участие ты
      не идёшь.
    </p>
  );
}

function RuleCard({ stats }: { stats: MyAttendance }) {
  if (!stats.barred) return null;
  return (
    <section className="refusal card--toned card--toned--outline" data-tone="danger">
      <p className="label label--tone">{missesInARow(stats.misses_streak)}</p>
      <p className="refusal-fact">
        {stats.next_service_start === null
          ? 'На ближайшее участие ты не идёшь'
          : `На участие ${formatLongDate(stats.next_service_start)} ты не идёшь`}
      </p>
      {/* The frame names the next rehearsal here — «Спевка 28 августа этого не
          меняет» — and this screen does not hold the schedule. The clause that
          matters is the one after the colon, so it is said without a date
          rather than bought with a request «Я» has never made. */}
      <p className="refusal-note">
        Пропущены {joinDates(stats.missed_starts)}. Счёт обнулится, когда участие пройдёт: спевки
        до участия этого не меняют.
      </p>
      <p className="refusal-note">
        Приложение не запрещает — если всё-таки участвуешь, скажи регенту.
      </p>
    </section>
  );
}

/**
 * Everything about one kind of gathering: the figure, the last ten, the tiles.
 *
 * One block and one heading, because the segment above it has already said
 * which kind is being counted. It was two of these stacked - «Я · спевки» over
 * «Я · участия» - so the word «Я» stood on the screen three times, and the
 * second stack was a second answer to a question nobody had asked twice.
 *
 * `rule` is always the rehearsal answer, whichever half the segment is showing:
 * the two-rehearsal rule counts practices and is about services, so it has one
 * value on this screen rather than one per kind.
 */
function MyFigures({
  stats,
  rule,
}: {
  stats: MyAttendance;
  rule: MyAttendance | null;
}) {
  if (stats.total === 0) {
    return (
      // Not "после первой закрытой": nothing in the statistics filters on
      // closed_at, so a row counts from the moment somebody is marked. A singer
      // who checked in ten minutes ago and read that she had to wait for the
      // rehearsal to end would be waiting for something that has already
      // happened.
      <Empty
        fact={`Отмеченных ${stats.kind === 'service' ? 'участий' : 'спевок'} пока нет`}
        note="Цифры появятся после первой спевки, на которой тебя отметят."
      />
    );
  }

  const since =
    stats.total === 1 ? words(stats.kind).genitive : words(stats.kind).genitivePlural;
  return (
    <div className="mine">
      {/* The one figure the screen exists for, at the size of an answer. «был
          за 11 спевок с 21 мая» is «спевок с 21 мая»: the preposition was
          wrong, and the figure above the line already says how many. */}
      <div className="hero">
        <p className="hero-figure">
          <span className="figure figure--hero figure--accent">{stats.attended}</span>
          <span className="figure-of">из {stats.total}</span>
        </p>
        <p className="hero-note">
          {since} с {formatLongDate(stats.period_start)} · серия {stats.streak}, рекорд{' '}
          {stats.best_streak}
        </p>
        {stats.recent.length > 0 && (
          // Ten strips rather than ten rows: a shape the eye takes in at once,
          // where a list of ten titles and dates is a screenful nobody scrolls
          // to the end of. Inside the card the figure is on, because they are
          // the same fact drawn twice - the total, and how it was arrived at.
          <div className="strips">
            {stats.recent.map((item) => (
              <span
                key={item.session_id}
                className="strip-mark"
                data-status={item.status}
                title={`${item.title ?? words(stats.kind).titled}, ${formatLongDate(
                  item.start,
                )} — ${statusLabel(item)}`}
              />
            ))}
          </div>
        )}
        {/* The rule's own footnote, and only on the half it counts: it is a
            second note to the same figure rather than a continuation of the
            first. */}
        {stats.kind === 'rehearsal' && rule !== null && <RuleNote stats={rule} />}
      </div>

      {/* The card the rule earns, directly under the figure and inside the
          group: it is about the person these numbers belong to. Drawn from the
          rehearsal answer whichever half of the segment is showing, because
          what it says is about a service. */}
      {rule !== null && <RuleCard stats={rule} />}

      {/* The footnotes to it. Lateness and excused absences share a tile
          because they are the two things that are not failures and should not
          be given one each to brood over. The share had a tile of its own until
          2026-09-04 and has the block above instead. */}
      <div className="tiles">
        {stats.kind !== 'rehearsal' && (
          <div className="figures">
            <p className="figure figure--stat figure--accent">
              {stats.attendance_rate_30d === null
                ? '—'
                : `${Math.round(stats.attendance_rate_30d * 100)}%`}
            </p>
            {/* Not «улыбка аватара»: the face follows rehearsals, and this half
                of the segment counts services. */}
            <p className="tile-caption">за 30 дней</p>
          </div>
        )}
        <div className="figures">
          <p className="figure figure--stat">
            {stats.late}·{stats.excused_absences}
          </p>
          {/* The words agree with the figures beside them: «опозданий ·
              уважит.» was an abbreviation standing over a «1». */}
          <p className="tile-caption">
            {plural(stats.late, ['опоздание', 'опоздания', 'опозданий'])} ·{' '}
            {plural(stats.excused_absences, ['уважительная', 'уважительные', 'уважительных'])}
          </p>
        </div>
      </div>

      {/* Last, and inside the group rather than between two of them: it split
          the figures it celebrates from the strips that show them. */}
      <StreakMilestone stats={stats} />
    </div>
  );
}

function SummaryCard({ summary }: { summary: ChoirAttendanceSummary }) {
  if (summary.total === 0) {
    return (
      <div className="block">
        <span className="label">По хору</span>
        <p className="hint">
          Отмеченных {summary.kind === 'service' ? 'участий' : 'спевок'} пока нет.
        </p>
      </div>
    );
  }

  const share = (attended: number, total: number): string =>
    total === 0 ? '—' : `${Math.round((attended / total) * 100)}%`;
  return (
    <section className="figures">
      <h2 className="figures-title">Хор в цифрах, без имён</h2>
      {/* Two monospaced lines rather than a list of rows. There are no names
          here and there never will be: the conversation about who shows up
          belongs to the regent, not to the app. */}
      <p className="figures-line">
        {summary.sessions}{' '}
        {plural(summary.sessions, [
          words(summary.kind).nominative,
          words(summary.kind).genitive,
          words(summary.kind).genitivePlural,
        ])}{' '}
        · средняя {share(summary.attended, summary.total)} ·{' '}
        {summary.members} {plural(summary.members, ['человек', 'человека', 'человек'])}
      </p>
      <p className="figures-line">
        {summary.by_voice_part
          .map(
            (part: VoicePartAttendance) =>
              `${voicePartLetter(part.voice_part)} ${share(part.attended, part.total)}`,
          )
          .join(' · ')}
      </p>
    </section>
  );
}

/**
 * The person holding the phone: their name, their voice part, their reminders,
 * their attendance.
 *
 * It was «Посещаемость» with a bar chart for an icon, and the profile card at
 * the top of it was invisible: the owner of the project asked for a profile tab
 * to be built while looking straight at one. The tab was also carrying the
 * choir's whole matrix, so it answered two questions about two different
 * subjects; the matrix now lives on «Хор» with the rest of the choir.
 */
export default function MeScreen({
  choir,
  home,
  onProfileChanged,
}: {
  choir: ChoirBrief;
  /** The lit tab, tapped again. */
  home: number;
  onProfileChanged: () => void;
}) {
  const isRegent = choir.role === 'regent';
  /**
   * What has just been done and can still be taken back — rotating the invite
   * link, which is the one reversible thing on this screen. The bar belongs to
   * the screen rather than to the card, the same rule «Хор» follows.
   */
  const [done, setDone] = useState<Done | null>(null);
  /** A number per action, so a second one restarts the five seconds. */
  const acts = useRef(0);
  const report = useCallback((text: string, undo: () => void) => {
    acts.current += 1;
    setDone({ id: acts.current, text, undo });
  }, []);
  const [mine, setMine] = useState<MyAttendance | null>(null);
  const [mineServices, setMineServices] = useState<MyAttendance | null>(null);
  const [summary, setSummary] = useState<ChoirAttendanceSummary | null>(null);
  const [summaryServices, setSummaryServices] = useState<ChoirAttendanceSummary | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  /** The role the totals on screen were asked for. */
  const loadedBy = useRef<boolean | null>(null);
  /**
   * Which kind of gathering the figures are about.
   *
   * A segment over one block rather than two stacks of figures under each
   * other. Services are regular in this choir, so they are not a second story -
   * they are the same question asked about a different set of gatherings, and
   * the shape for that in this app is a `.seg`. Both answers are already in
   * hand: the screen makes the same four requests it made before.
   */
  const [kind, setKind] = useState<SessionKind>('rehearsal');
  /** One's own record, which is a screen behind the pencil on the face. */
  const [page, setPage] = useState<'me' | 'profile'>('me');
  useHome(home, () => setPage('me'));

  const load = useCallback(async () => {
    // Reset before loading: switching choirs can change the role, and without
    // this the previous role's block would stay on screen.
    //
    // C-023 (`FND-1618`): the reset is for a role that changed, and the role is in the
    // key above this screen, so inside one mount `load` is only ever the same function
    // run again — on a return to the tab, now that the tab stays mounted. A block that
    // vanished for the length of the request on every return was the jerk the audit
    // of 2026-10-27 measured; the totals stay on screen and the fresh answer replaces them.
    if (loadedBy.current !== isRegent) {
      loadedBy.current = isRegent;
      setSummary(null);
      setSummaryServices(null);
    }
    try {
      // The anonymous totals are for a singer only. The regent has the whole
      // matrix, by name, on the choir screen — putting the same question twice
      // on two screens is how the numbers start disagreeing.
      //
      // Four requests where there were two, because the two kinds are counted
      // apart and each answer is about one of them. In parallel, so the screen
      // waits exactly as long as it did.
      const [personal, personalServices, choirTotals, choirServices] = await Promise.all([
        api.myAttendance('rehearsal'),
        api.myAttendance('service'),
        isRegent ? null : api.choirSummary('rehearsal'),
        isRegent ? null : api.choirSummary('service'),
      ]);
      setMine(personal);
      setMineServices(personalServices);
      setSummary(choirTotals);
      setSummaryServices(choirServices);
      setFailure(null);
    } catch (error) {
      setFailure(error instanceof ApiError ? error.message : 'Не удалось загрузить посещаемость');
    }
  }, [isRegent]);

  useEffect(() => {
    void load();
  }, [load]);

  const rate = mine?.attendance_rate_30d ?? null;

  /** The figures, and the profile form over them. Both branches, one `Push`. */
  const pushed = (screen: ReactNode): ReactNode => (
    <Push at={page} depth={page === 'me' ? 0 : 1}>
      {screen}
    </Push>
  );

  if (page === 'profile') {
    return pushed(
      <ProfileScreen
        choir={choir}
        rate={rate}
        streak={mine?.streak ?? 0}
        best={mine?.best_streak ?? 0}
        onChanged={onProfileChanged}
        onBack={() => setPage('me')}
      />,
    );
  }

  const shown = kind === 'service' ? mineServices : mine;
  const shownSummary = kind === 'service' ? summaryServices : summary;
  /* The segment only once there are services to switch to. A choir that never
     holds them sees the screen it always saw, rather than a control offering a
     second set of zeroes about a feature it does not use. */
  const hasServices = mineServices !== null && mineServices.total > 0;

  return pushed(
    <>
      {/* The rung the streak has just landed on, over the card that holds the
          ladder it belongs to: what it hands over is drawn a few lines below,
          so the two have to arrive together. Rehearsals whichever half is
          showing, for the reason the card itself counts them. */}
      {mine !== null && <RungOpened choir={choir} stats={mine} />}

      {/* The person, their face and what the face is worth - one card, and it
          stands above the segment because it counts rehearsals whatever the
          segment says. It was a row here and a block down among the figures
          until 2026-09-04; see `MyProfileCard`. */}
      <MyProfileCard
        choir={choir}
        stats={mine}
        pending={mine === null && failure === null}
        onOpenProfile={() => setPage('profile')}
      />

      {hasServices && (
        <div className="seg">
          <button
            className={kind === 'rehearsal' ? 'btn btn--selected' : 'btn btn--outline'}
            onClick={() => setKind('rehearsal')}
          >
            Спевки
          </button>
          <button
            className={kind === 'service' ? 'btn btn--selected' : 'btn btn--outline'}
            onClick={() => setKind('service')}
          >
            Участия
          </button>
        </div>
      )}

      {/* Nothing at all, or why. Once the figures are on screen a failed
          reload leaves them: they are still true of the day they were read. */}
      {mine === null && failure === null && <Skeleton />}
      {mine === null && failure !== null && (
        <Failed subject="attendance" onRetry={() => void load()} />
      )}
      {shown !== null && (
        <Settling>
          <MyFigures stats={shown} rule={mine} />
        </Settling>
      )}

      {/* Last, because it is about the app rather than about the person: what
          they came to look at is the figures above it. */}
      <RemindersRow choir={choir} />

      {/* The choir's own totals, with no names in them. A singer's, because
          the regent has the whole matrix by name on «Хор» and asking the same
          question twice on two screens is how the numbers start disagreeing. */}
      {shownSummary !== null && <SummaryCard summary={shownSummary} />}

      {/* The invite link, and this is where it lives since 2026-08-22. It was
          the last block of «Хор», which was the regent's tab; «Хор» is open to
          a singer now, and an invitation needs a screen a singer does not
          share. «Я» is that screen, and the link stands beside the role it
          belongs to. */}
      {isRegent && <InviteCard onDone={report} />}

      <UndoBar done={done} onGone={() => setDone(null)} />
    </>,
  );
}
